/* The Cardea authorization-code + PKCE flow, with no framework in it.

   Deliberately plain: no router, no state library, no OIDC client package. The
   module owns the session (an in-memory access token, a refresh token in
   sessionStorage, one refresh timer) and publishes changes to whoever
   subscribed. AuthProvider.tsx is the only React in the auth module, and it is
   a thin binding over this file.

   The shape of a sign-in:

     beginSignIn()        → store {state, verifier, nonce}, redirect to Cardea
     …user authenticates…
     completeCallback()   → validate state, exchange code, scrub the URL
     getToken()           → a live access token for the API client, refreshing
                            if it is expired or close to it
     signOut()            → forget everything, back to the sign-in page */

import { authConfig, CALLBACK_PATH, SCOPES } from './config'
import { createCodeChallenge, createCodeVerifier, createOpaqueToken } from './pkce'
import { decodeJwtPayload, displayNameFrom, expiryFromToken } from './jwt'
import {
  clearAll,
  readAccessToken,
  readRefreshToken,
  readUserName,
  takePending,
  writeAccessToken,
  writePending,
  writeRefreshToken,
  writeUserName,
} from './storage'

/* Refresh this far ahead of expiry. Covers clock skew between the browser and
   Cardea, plus the round-trip itself, so a request never leaves with a token
   that expires while it is in flight. */
const REFRESH_MARGIN_MS = 60_000

/* setTimeout coerces delays above 2^31-1 ms to 1, firing immediately — a real
   bug in a scheduler, even if a Cardea access token's hour makes it unreachable
   here. Clamped rather than assumed. */
const MAX_TIMEOUT_MS = 2_147_483_647

/* Bound the token request. Without this a token endpoint that accepts the
   connection and then never answers pins `inFlightRefresh` forever, and every
   later getToken() awaits the same hung promise. */
const TOKEN_REQUEST_TIMEOUT_MS = 20_000

/* How long to wait before retrying a refresh that failed to reach Cardea at
   all. Short, because the access token is already inside its refresh margin;
   bounded, because a device that is offline will keep failing. */
const TRANSPORT_RETRY_MS = 30_000

/** Called with the display name on sign-in, and with null when the session ends. */
export type SessionListener = (user: string | null) => void

const listeners = new Set<SessionListener>()

let refreshTimer: ReturnType<typeof setTimeout> | null = null
let inFlightRefresh: Promise<string | null> | null = null
let callbackExchange: Promise<string> | null = null
let signInRedirect: Promise<void> | null = null

/* Incremented by endSession(). Any request that was already in flight when the
   session ended compares the generation it started in against this one and
   refuses to write its result back.

   Without it, signing out during a refresh is silently undone: endSession()
   clears storage and notifies, then the token response arrives, adoptTokenResponse()
   writes the tokens back, re-arms the timer and notifies a name — resurrecting the
   session the user just ended. Sign-out has to be final. */
let sessionGeneration = 0

/** Subscribe to session changes. Returns the unsubscribe. */
export function onSessionChange(listener: SessionListener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/* Every listener is called, and a throw from one is contained.
   A subscriber that throws would otherwise escape into whichever flow called
   notify(): on the callback path it rejects completeCallback() *after* the
   credentials were stored, showing an error for a session that is live; on the
   refresh path it is caught by refreshAccessToken's catch, which ends the
   session and clears the credentials it had just refreshed. One faulty
   subscriber must not be able to sign everybody out. */
function notify(user: string | null): void {
  for (const listener of listeners) {
    try {
      listener(user)
    } catch (cause) {
      // Reported, not swallowed: a listener throwing is a bug in the listener.
      console.error('A session listener threw. Session state is unaffected.', cause)
    }
  }
}

// ── Token endpoint ───────────────────────────────────────────────────────────

interface TokenResponse {
  access_token: string
  token_type?: string
  expires_in?: number
  refresh_token?: string
  id_token?: string
  scope?: string
}

/**
 * Cardea answered and refused the grant.
 *
 * The distinction this type exists to draw: a `TokenGrantError` proves the
 * credential is spent, so ending the session is the only useful response. Any
 * *other* rejection out of `postToken` — DNS failure, offline, a laptop waking
 * mid-timer, the request timing out — proves nothing about the credential, and
 * a refresh token valid for another 30 days must survive it.
 */
class TokenGrantError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TokenGrantError'
  }
}

/**
 * Does this response prove the *credential* was refused?
 *
 * Read the OAuth error code, not just the status. RFC 6749 §5.2 gives the token
 * endpoint six of them, and **only `invalid_grant` is about the credential** —
 * it is the one Cardea returns for a refresh token that is revoked, expired or
 * already rotated. The other five describe the request or the client
 * registration while the refresh token stays perfectly valid:
 *
 *   invalid_request, invalid_client, unauthorized_client,
 *   unsupported_grant_type, invalid_scope
 *
 * Classifying those as a rejection deletes a refresh token good for another 30
 * days over, say, a mistyped `client_id` — and does it from the unattended
 * refresh timer, so the user is signed out at an arbitrary moment with no action
 * to explain it and nothing to fix. `endSession()` is irreversible here; the
 * only recovery is a full trip through Cardea.
 *
 * So the rule the previous status-only version stated but did not follow:
 * erring towards `false` costs a retry, erring towards `true` costs the session,
 * and every ambiguous case belongs on the `false` side. A 4xx whose body is not
 * JSON, or carries no `error`, is ambiguous — Cardea always sends one, so its
 * absence means something unexpected answered, not that the grant was refused.
 *
 * The status is still required to be 4xx: a 5xx is Cardea's fault whatever it
 * puts in the body.
 *
 * This is only consulted on `!response.ok`, and it changes nothing on the
 * authorization-code path — `runCallback` treats `TokenGrantError` and `Error`
 * identically (both reject `completeCallback` with the same message). It is the
 * refresh path, and only the refresh path, that acts on the distinction.
 */
function isGrantRejection(status: number, payload: unknown): boolean {
  if (status < 400 || status >= 500) return false
  if (!payload || typeof payload !== 'object') return false
  return (payload as Record<string, unknown>)['error'] === 'invalid_grant'
}

/**
 * POST to Cardea's token endpoint.
 *
 * ┌─ CORS TRAP — READ THIS BEFORE CHANGING THIS FETCH ────────────────────────┐
 * │ Cardea's token endpoint answers preflight-relevant CORS with:             │
 * │                                                                          │
 * │     Access-Control-Allow-Origin:  *                                      │
 * │     Access-Control-Allow-Methods: GET, POST                              │
 * │     Access-Control-Allow-Headers: Authorization                          │
 * │                                                                          │
 * │ `Content-Type` is NOT in that allow list. So this request MUST stay a     │
 * │ CORS **simple request** — one the browser sends without a preflight:      │
 * │                                                                          │
 * │   • `Content-Type: application/x-www-form-urlencoded` (a safelisted       │
 * │     value, so it does not itself trigger a preflight), and                │
 * │   • NO other headers. None. Not `Accept`, not `X-Requested-With`, not a   │
 * │     trace header, not anything a future interceptor adds.                 │
 * │                                                                          │
 * │ Send JSON, or add one custom header, and the browser issues an OPTIONS    │
 * │ preflight; Cardea's `Allow-Headers` does not cover it; the preflight      │
 * │ fails; and the whole sign-in dies with a generic "Failed to fetch" and    │
 * │ nothing in the response to explain it. Verified live 2026-08-04.          │
 * │                                                                          │
 * │ This is the single most likely thing to be broken by a well-meaning       │
 * │ edit. If sign-in stops working right after someone "tidied up the API     │
 * │ layer", look here first.                                                  │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * The client is **public** — no secret exists. `client_id` goes in the body and
 * no client authentication is sent, which is exactly what
 * `token_endpoint_auth_methods_supported: [..., "none"]` advertises.
 */
async function postToken(endpoint: string, body: URLSearchParams): Promise<TokenResponse> {
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
    // `signal` is a fetch option, not a request header, so this does not
    // disturb the simple-request shape the box above insists on.
    signal: AbortSignal.timeout(TOKEN_REQUEST_TIMEOUT_MS),
  })

  const text = await response.text()
  let payload: unknown = null
  try {
    payload = JSON.parse(text)
  } catch {
    /* Not JSON. Handled below by falling back to the status line. */
  }

  if (!response.ok) {
    const message = describeTokenError(payload, response.status, text)
    // Only a response that proves the grant was refused may end the session.
    throw isGrantRejection(response.status, payload)
      ? new TokenGrantError(message)
      : new Error(message)
  }
  const token = payload as TokenResponse | null
  if (!token || typeof token.access_token !== 'string') {
    throw new Error('Cardea returned a token response with no access_token.')
  }
  return token
}

/** Turn an OAuth error body into something a human can act on. */
function describeTokenError(payload: unknown, status: number, raw: string): string {
  if (payload && typeof payload === 'object') {
    const body = payload as Record<string, unknown>
    const code = typeof body['error'] === 'string' ? body['error'] : null
    const detail =
      typeof body['error_description'] === 'string' ? body['error_description'] : null
    if (code && detail) return `Cardea rejected the token request: ${code} — ${detail}`
    if (code) return `Cardea rejected the token request: ${code}`
  }
  const snippet = raw.slice(0, 200).trim()
  return `Cardea's token endpoint returned ${status}${snippet ? `: ${snippet}` : '.'}`
}

/**
 * When this access token stops being usable, in epoch ms.
 *
 * Taken from `expires_in` measured at the moment the response arrived, falling
 * back to the JWT's own `exp`.
 *
 * **Never from `iat`.** Pocket ID's refresh grant preserves the original
 * authorization's `iat` and advances only `exp`, so `exp - iat` grows with every
 * refresh and anything computing a lifetime or an age from `iat` misjudges every
 * refreshed token — silently, and by more the longer the session lives.
 */
function expiresAtFrom(token: TokenResponse, receivedAt: number): number {
  if (typeof token.expires_in === 'number' && Number.isFinite(token.expires_in)) {
    return receivedAt + token.expires_in * 1000
  }
  return expiryFromToken(token.access_token) ?? receivedAt + 5 * 60_000
}

/** Store a token response, re-arm the refresh timer, and return the display name. */
function adoptTokenResponse(token: TokenResponse, receivedAt: number): string {
  const expiresAt = expiresAtFrom(token, receivedAt)
  writeAccessToken({ value: token.access_token, expiresAt })

  // Cardea may rotate the refresh token on use. Keep the old one if it does not.
  if (token.refresh_token) writeRefreshToken(token.refresh_token)

  // The ID token is the one that carries a human name; the access token issued
  // for an RFC 8707 resource may only have `sub`. A refresh does not always
  // return a new ID token, so keep the name we already have rather than
  // regressing to a UUID.
  const name = displayNameFrom(token.id_token, token.access_token) ?? readUserName() ?? 'signed in'
  writeUserName(name)

  scheduleRefresh(expiresAt)
  return name
}

// ── Refresh ──────────────────────────────────────────────────────────────────

function cancelRefreshTimer(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

/**
 * Cancel the pending refresh.
 *
 * Exported for AuthProvider's effect cleanup. Safe to call at any time: the
 * provider's mount path always re-arms the timer from the live token's expiry,
 * which is what makes React 19 StrictMode's mount → unmount → mount in dev a
 * no-op here rather than a session that silently stops refreshing.
 */
export function stopRefreshTimer(): void {
  cancelRefreshTimer()
}

function scheduleRefresh(expiresAt: number): void {
  cancelRefreshTimer()
  const delay = Math.min(Math.max(expiresAt - Date.now() - REFRESH_MARGIN_MS, 1_000), MAX_TIMEOUT_MS)
  refreshTimer = setTimeout(() => {
    void refreshAccessToken()
  }, delay)
}

/**
 * Exchange the refresh token for a new access token.
 *
 * **A rejected grant ends the session; a failed request does not.** A refresh
 * that Cardea refused because the token was revoked, expired or already rotated
 * will be refused every subsequent time, and a retry loop against an
 * authorization server is both useless and rude — the user goes back to the
 * sign-in page, which is the only thing that can fix it. But a request that
 * never arrived (offline, DNS, a laptop waking mid-timer, a 5xx) — or one
 * Cardea refused for a reason that is not about the credential at all (a
 * mistyped `client_id`, a scope the registration no longer grants) — says
 * nothing about the refresh token, and `endSession()` would delete one still
 * valid for 30 days. This timer fires unattended, so that sign-out would land
 * at an arbitrary moment with no user action to explain it. See
 * `isGrantRejection` for where the line is drawn.
 */
function refreshAccessToken(): Promise<string | null> {
  // Collapse concurrent callers onto one request. Several components calling
  // getToken() at once on a cold reload is the normal case, and a rotating
  // refresh token would make the losers of that race fail.
  if (inFlightRefresh) return inFlightRefresh

  // Read before the first await: everything below compares against it to decide
  // whether the session it belongs to still exists.
  const generation = sessionGeneration

  inFlightRefresh = (async () => {
    try {
      // Inside the try, so the `finally` always runs. Returning early from
      // above it would leave `inFlightRefresh` pinned to a settled promise and
      // every later call would get that stale null back.
      const refreshToken = readRefreshToken()
      if (!refreshToken) return null

      const cfg = authConfig()
      const receivedAt = Date.now()
      const token = await postToken(
        cfg.tokenEndpoint,
        new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
          client_id: cfg.clientId,
          // RFC 8707 on the refresh too, so the new token carries the same
          // audience the backend validates against. `scope` is omitted, which
          // asks for the originally granted set.
          resource: cfg.resource,
        }),
      )

      // signOut() ran while that request was in flight. Adopting the response
      // now would re-store the tokens, re-arm the timer and announce a name,
      // undoing the sign-out. Drop it on the floor instead.
      if (generation !== sessionGeneration) return null

      const name = adoptTokenResponse(token, receivedAt)
      notify(name)
      return token.access_token
    } catch (cause) {
      // The session already ended: neither ending it again nor re-arming a
      // timer for it is right.
      if (generation !== sessionGeneration) return null
      if (cause instanceof TokenGrantError) {
        endSession()
      } else {
        // The credential is untouched — keep it and try again shortly.
        scheduleRefresh(Date.now() + REFRESH_MARGIN_MS + TRANSPORT_RETRY_MS)
      }
      return null
    } finally {
      // Only clear our own entry. endSession() has already nulled the slot and
      // a newer refresh may be sitting in it by now.
      if (generation === sessionGeneration) inFlightRefresh = null
    }
  })()

  return inFlightRefresh
}

// ── The public flow ──────────────────────────────────────────────────────────

/** True when this page load is Cardea redirecting back to us. */
export function isCallbackUrl(): boolean {
  return window.location.pathname === CALLBACK_PATH
}

/**
 * Start a sign-in: build the authorization request and leave for Cardea.
 *
 * Resolves only if the redirect could not be started; on success the page is
 * already navigating away. Rejects with `AuthConfigError` when the client id is
 * missing, which the caller surfaces on the sign-in page.
 *
 * **Memoized, like `completeCallback`, for a different reason.** `writePending`
 * owns a SINGLE storage slot. Two overlapping calls each finish their own
 * `createCodeChallenge`, each overwrite that slot, and each call
 * `location.assign` — so the browser navigates to the first call's
 * authorization URL while the second call's `state` is what is left in storage.
 * The callback then fails the state check and rejects a sign-in that was
 * entirely valid. Two rapid `signIn()` invocations are enough.
 *
 * The memo is dropped only on failure, so a legible error (a missing client id)
 * can be retried. On success there is nothing to drop: the page is leaving.
 */
export function beginSignIn(): Promise<void> {
  if (!signInRedirect) {
    signInRedirect = startSignIn().catch((cause: unknown) => {
      signInRedirect = null
      throw cause
    })
  }
  return signInRedirect
}

async function startSignIn(): Promise<void> {
  const cfg = authConfig()

  const codeVerifier = createCodeVerifier()
  const state = createOpaqueToken()
  const nonce = createOpaqueToken()
  const codeChallenge = await createCodeChallenge(codeVerifier)

  // Written before the redirect, obviously — memory does not survive it.
  writePending({ state, codeVerifier, nonce })

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cfg.clientId,
    redirect_uri: cfg.redirectUri,
    scope: SCOPES,
    state,
    nonce,
    code_challenge: codeChallenge,
    // Explicit, always. Cardea would accept `plain`; OAuth 2.1 would not.
    code_challenge_method: 'S256',
    // RFC 8707: ask for a token audienced at Milo, not a general-purpose one.
    resource: cfg.resource,
  })

  window.location.assign(`${cfg.authorizationEndpoint}?${params.toString()}`)
}

/**
 * Finish the redirect back from Cardea. Resolves to the display name.
 *
 * Memoized on the module, which is what makes React 19 StrictMode safe: the
 * effect that calls this runs twice in dev, and a second exchange of the same
 * authorization code is *guaranteed* to fail (codes are single-use, and Cardea
 * is right to reject a replay). Both callers await the same promise, so exactly
 * one exchange happens and a StrictMode double-invoke can never be reported to
 * the user as a failed sign-in.
 */
export function completeCallback(): Promise<string> {
  if (!callbackExchange) callbackExchange = runCallback()
  return callbackExchange
}

/**
 * The callback exchange this page load already started, or null.
 *
 * **Never starts one.** That is the whole point: `completeCallback()` would
 * begin an exchange on a page that is not a callback. The one caller is
 * AuthProvider's effect, which uses it to re-attach on React 19 StrictMode's
 * second mount — by then `runCallback` has scrubbed the URL, so `isCallbackUrl()`
 * is false and the second mount would otherwise never learn how the first one
 * ended.
 */
export function pendingCallback(): Promise<string> | null {
  return callbackExchange
}

async function runCallback(): Promise<string> {
  const params = new URLSearchParams(window.location.search)

  // One-time values, consumed whether or not the rest of this succeeds.
  const pending = takePending()

  /* Scrub before anything can throw. `code` and `state` must not survive in the
     address bar, in session history (where a back-navigation would re-run this
     against a spent code), or in a `Referer` sent to the first third-party
     resource this page happens to load. replaceState rather than pushState:
     the callback URL should not be a place the back button can return to. */
  window.history.replaceState(null, '', '/')

  const error = params.get('error')
  if (error) {
    const description = params.get('error_description')
    throw new Error(description ? `${error} — ${description}` : `Cardea returned "${error}".`)
  }

  /* CSRF guard. `state` ties this response to the request *this* browser
     started; without the check, an attacker can hand the user a callback URL
     carrying their own authorization code and silently sign the user into the
     attacker's account. A missing or mismatched state must never reach a token
     exchange — there is no lenient path here on purpose. */
  const state = params.get('state')
  if (!pending) {
    throw new Error(
      'No sign-in was in progress in this tab, so this callback cannot be trusted. Start again.',
    )
  }
  if (!state || state !== pending.state) {
    throw new Error(
      'The sign-in response did not match the request that started it (state mismatch). ' +
        'Nothing was exchanged. Start again.',
    )
  }

  const cfg = authConfig()

  /* RFC 9207. Cardea advertises `authorization_response_iss_parameter_supported`,
     so when `iss` is present it is checked against the issuer we sent the user
     to — the mix-up defence for an estate that may one day have more than one
     authorization server. Absent, we proceed: it is an optional parameter. */
  const iss = params.get('iss')
  if (iss && iss.replace(/\/+$/, '') !== cfg.issuer) {
    throw new Error(`The sign-in response came from an unexpected issuer (${iss}).`)
  }

  const code = params.get('code')
  if (!code) throw new Error('Cardea returned no authorization code.')

  const receivedAt = Date.now()
  const token = await postToken(
    cfg.tokenEndpoint,
    new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: cfg.redirectUri,
      client_id: cfg.clientId,
      code_verifier: pending.codeVerifier,
      resource: cfg.resource,
    }),
  )

  /* OIDC Core §3.1.3.7 step 11. The `nonce` ties the returned ID token to the
     authorization request *this* tab started, so an ID token lifted from some
     other authorization cannot be passed off as the answer to ours.

     Checked when the claim is present, and tolerated when it is absent. That
     asymmetry is deliberate: a *differing* nonce is an attack and there is no
     benign reading of it, while an *absent* one would only ever mean Pocket ID
     does not echo the parameter — a behaviour not verified against the live
     Cardea, and one that a hard requirement would turn into a total sign-in
     outage on deploy. Nothing in Milo trusts an ID token claim for a decision
     (see jwt.ts), so the tolerated case costs nothing today. If an ID token
     claim ever becomes load-bearing, confirm the claim is echoed and make this
     mandatory. */
  const idTokenNonce = token.id_token ? decodeJwtPayload(token.id_token)?.['nonce'] : undefined
  if (idTokenNonce !== undefined && idTokenNonce !== pending.nonce) {
    throw new Error(
      'The ID token does not match the sign-in request that asked for it (nonce ' +
        'mismatch). Nothing was kept. Start again.',
    )
  }

  const name = adoptTokenResponse(token, receivedAt)
  notify(name)
  return name
}

/**
 * Re-establish a session on a cold page load, or confirm the live one.
 *
 * Resolves to the display name, or null when there is nothing to restore. The
 * access token never survives a reload by design, so this is where the one
 * refresh round-trip described in storage.ts is spent.
 */
export async function restoreSession(): Promise<string | null> {
  const token = readAccessToken()
  if (token && Date.now() < token.expiresAt - REFRESH_MARGIN_MS) {
    // Live already — but re-arm, because a StrictMode remount cleared the timer.
    scheduleRefresh(token.expiresAt)
    return readUserName()
  }
  if (!readRefreshToken()) return null
  const refreshed = await refreshAccessToken()
  return refreshed ? readUserName() : null
}

/**
 * A live access token for the API client, or null if there is no session.
 *
 * Refreshes first when the token is expired or inside the refresh margin, so a
 * caller never has to think about expiry. See index.ts — this signature is the
 * contract with the API client.
 */
export async function getToken(): Promise<string | null> {
  const token = readAccessToken()
  const now = Date.now()

  if (token && now < token.expiresAt - REFRESH_MARGIN_MS) return token.value
  if (readRefreshToken()) return refreshAccessToken()

  // No refresh token, so nothing can extend this session. A still-valid token
  // inside the margin is better than no token; an expired one is not, and
  // handing it out would only produce a 401 somewhere less legible.
  if (token && now < token.expiresAt) return token.value
  if (token) endSession()
  return null
}

/**
 * Drop the session locally and tell everyone.
 *
 * Bumping `sessionGeneration` is what makes this final. Clearing storage is not
 * enough on its own: a token request that was already in flight would land
 * afterwards and write its result straight back in. The generation invalidates
 * those in-flight results rather than trying to cancel them.
 */
function endSession(): void {
  sessionGeneration += 1
  cancelRefreshTimer()
  callbackExchange = null
  inFlightRefresh = null
  clearAll()
  notify(null)
}

/**
 * Sign out.
 *
 * Clears the in-memory access token, everything in sessionStorage, and the
 * refresh timer, then notifies — which puts the app back on the sign-in page.
 *
 * **Cardea's `end_session_endpoint` is deliberately not called.** It *is*
 * advertised (checked 2026-08-04: `https://cardea.rayray.cloud/api/oidc/end-session`),
 * so this is a choice rather than an absence. Two reasons:
 *
 *  - RP-initiated logout is a full-page navigation to Cardea, and it ends the
 *    *Cardea* session — the shared SSO session behind every Serapeum service in
 *    this browser. "Sign out of Milo" should not sign the user out of Warburg
 *    and Champollion as a side effect.
 *  - Coming back requires a registered `post_logout_redirect_uri`, which no one
 *    has registered for Milo, so the user would be stranded on Cardea.
 *
 * The consequence, stated honestly: after signing out, signing back in may not
 * prompt for a passkey, because the Cardea session is still valid. What is
 * destroyed here is Milo's copy of the credentials, which is what actually
 * matters for a shared machine. If a real "sign out everywhere" is ever wanted,
 * register a post-logout redirect and send the user to that endpoint with an
 * `id_token_hint`.
 */
export function signOut(): void {
  endSession()
}
